import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { formatEventDateTime, truncateTitle } from "../../utilities/reusableItems";
import { Nav, Tab } from "react-bootstrap";
import Spinner from "../Spinner";

const EventList = ({ user }) => {
	const [events, setEvents] = useState([]);
	const [usersMap, setUsersMap] = useState({});
	const [loading, setLoading] = useState(true);
	const [imagesLoaded, setImagesLoaded] = useState(0);
	const [totalImages, setTotalImages] = useState(0);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const eventSnapshot = await getDocs(collection(db, "events"));
				const eventList = eventSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
				setEvents(eventList);
				setTotalImages(eventList.length); // one image per event card

				const usersSnapshot = await getDocs(collection(db, "users"));
				const usersData = {};
				usersSnapshot.docs.forEach((doc) => {
					usersData[doc.id] = doc.data();
				});
				setUsersMap(usersData);
			} catch (err) {
				console.error("Error fetching events or users:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const handleImageLoad = () => setImagesLoaded((prev) => prev + 1);
	const allImagesLoaded = totalImages === 0 || imagesLoaded >= totalImages;

	const handleViewEvent = (eventId) => {
		navigate(`/event/${eventId}`);
	};

	// Split events into upcoming and past, move to past events after 24hrs of event date
	const now = new Date();
	const getEventDateTime = (event) => {
		const timeStr = event.endTime || event.startTime || event.time || "00:00";
		const eventEnd = new Date(`${event.date} ${timeStr}`);
		eventEnd.setHours(eventEnd.getHours() + 24);
		return eventEnd;
	};
	const upcomingEvents = events
		.filter((event) => getEventDateTime(event) >= now)
		.sort(
			(a, b) =>
				new Date(`${a.date} ${a.startTime || a.time || "00:00"}`) -
				new Date(`${b.date} ${b.startTime || b.time || "00:00"}`),
		);

	const pastEvents = events
		.filter((event) => getEventDateTime(event) < now)
		.sort(
			(a, b) =>
				new Date(`${b.date} ${b.startTime || b.time || "00:00"}`) -
				new Date(`${a.date} ${a.startTime || a.time || "00:00"}`),
		);

	if (loading) return <Spinner />;

	const renderEventCard = (event, isPast = false) => {
		const creator = usersMap[event.userId];

		return (
			<div
				className="row hover-row event-card"
				onClick={() => handleViewEvent(event.id)}
				key={event.id}
				style={{ minHeight: "155px", height: "auto" }}
			>
				<div className="col">
					<div className="d-flex justify-content-between">
						<div className="d-flex flex-column events me-2 mb-2">
							<p className="card-text text-uppercase fw-bold my-color">
								{formatEventDateTime(event.date, event.startTime || event.time)}
								{event.endTime && ` - ${event.endTime}`}
							</p>
							<h5 className="d-flex align-items-center">
								{event.eventStatus === "canceled" && (
									<>
										<i className="bi bi-x-circle-fill text-danger me-2"></i>
										<span className="text-danger fw-bold me-2">EVENT CANCELLED:</span>
									</>
								)}
								{truncateTitle(event.title)}
							</h5>
							<p className="card-text">
								<i className="bi bi-geo-alt-fill me-1"></i>
								{event.name || "Not specified"}
							</p>
							<p className="card-text d-flex align-items-center text-muted mt-1">
								<i className="bi bi-person-raised-hand me-1" style={{ fontSize: "1rem" }}></i>
								<img
									src={creator?.photoURL || "/images/placeholder.png"}
									alt="Creator"
									className="rounded-circle me-2"
									style={{ width: "22px", height: "22px", objectFit: "cover" }}
								/>
								{creator?.displayName || event.createdBy}
							</p>
						</div>

						<div style={{ position: "relative", display: "inline-block" }}>
							<img
								src={event.imageUrl || "/images/placeholder.png"}
								alt="Event"
								className="rounded event-image mt-1"
								onLoad={handleImageLoad}
								onError={handleImageLoad} // count errors too so we don't hang
							/>
							<div className="position-absolute text-end text-muted mt-2">
								{event.attending?.includes(user.uid) && (
									<div className="d-flex align-items-center fs-6">
										<i className="bi bi-check-circle-fill me-2 attending-display text-success"></i>
										<span className="small text-nowrap text-success">{isPast ? "You went" : "You're going"}</span>
									</div>
								)}
								{event.notAttending?.includes(user.uid) && (
									<div className="d-flex align-items-center fs-6">
										<i className="bi bi-x-circle-fill me-2 attending-display text-danger"></i>
										<span className="small text-nowrap text-danger">{isPast ? "Didn't attend" : "Not going"}</span>
									</div>
								)}
								<div className="text-muted small mt-1">
									<i className="bi bi-people-fill me-1"></i>
									{event.attending?.length || 0} {isPast ? "Attended" : "Attending"}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="container mt-2 px-4 justify-content-center">
			<div className="col-md-8 col-lg-6 mx-auto">
				{!allImagesLoaded && <Spinner />}
				<div style={{ visibility: allImagesLoaded ? "visible" : "hidden" }}>
					<Tab.Container defaultActiveKey="upcoming">
						<Nav variant="tabs" className="mb-3">
							<Nav.Item>
								<Nav.Link eventKey="upcoming" className="custom-tab">
									Upcoming Events
								</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="past" className="custom-tab">
									Past Events
								</Nav.Link>
							</Nav.Item>
						</Nav>
						<Tab.Content>
							<Tab.Pane eventKey="upcoming">
								{upcomingEvents.length > 0 ? (
									upcomingEvents.map((event) => renderEventCard(event, false))
								) : (
									<p className="text-muted">No upcoming events.</p>
								)}
							</Tab.Pane>
							<Tab.Pane eventKey="past">
								{pastEvents.length > 0 ? (
									pastEvents.map((event) => renderEventCard(event, true))
								) : (
									<p className="text-muted">No past events.</p>
								)}
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</div>
			</div>
		</div>
	);
};

export default EventList;
